import {
  authWithApple,
  authWithFacebook,
  authWithGarmin,
  authWithGoogle,
  authWithPopupOAuth,
} from '@features/auth/model/actions.js';
import {
  changesetsInspect,
  changesetsReturnToHistory,
} from '@features/changesets/model/actions.js';
import {
  dataViewerDelete,
  dataViewerDeleteFeature,
  dataViewerDetachHole,
  dataViewerEditFeatures,
  dataViewerMakeHole,
  dataViewerSetData,
} from '@features/dataViewer/model/actions.js';
import { documentShow } from '@features/documents/model/actions.js';
import {
  objectsSetResult,
  setDetailsShown,
} from '@features/objects/model/actions.js';
import {
  routePlannerAddPoint,
  routePlannerDelete,
  routePlannerSetPoint,
} from '@features/routePlanner/model/actions.js';
import {
  searchSelectResult,
  searchUnselectResult,
} from '@features/search/model/actions.js';
import { trackingActions } from '@features/tracking/model/actions.js';
import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import { isDrawTool, isMapClickTool } from '@shared/toolDefinitions.js';
import { featureIdsEqual } from '@shared/types/featureId.js';
import {
  clearMapFeatures,
  closeTool,
  convertToDrawing,
  hideInfoBar,
  infoBarShown,
  openTool,
  type Selection,
  selectFeature,
  selectionRenumbered,
  setActiveModal,
  setEmbedFeatures,
  setErrorTicketId,
  type Tool,
} from './actions.js';
import type { ActiveModal } from './activeModal.js';

export interface MainState {
  /**
   * The open map-click tool — the owner of map clicks, and at most one of them,
   * so which toolbar takes a click never has to be stored separately.
   */
  mapTool: Tool | null;
  /**
   * The open tools that only bring a toolbar, in the order they were opened —
   * any number of them, alongside each other and alongside `mapTool`.
   */
  panelTools: Tool[];
  activeModal: ActiveModal | null;
  errorTicketId: string | undefined;
  embedFeatures: string[];
  selection: Selection | null;
  /** Whether the selected feature's details toast is wanted; set anew by every selecting. */
  detailsShown: boolean;
  /** The selection came without its details, so its toolbar offers them. */
  detailsOnRequest: boolean;
  hiddenInfoBars: Record<string, number>;
  shownInfoBars: Record<string, number>;
}

export const mainInitialState: MainState = {
  mapTool: null,
  panelTools: [],
  activeModal: null,
  errorTicketId: undefined,
  embedFeatures: [],
  selection: null,
  detailsShown: true,
  detailsOnRequest: false,
  hiddenInfoBars: {},
  shownInfoBars: {},
};

function clearTrackingSelection(state: MainState, token: string) {
  if (state.selection?.type === 'tracking' && state.selection.id === token) {
    state.selection = null;
  }
}

/**
 * Whether the selected feature belongs to `tool` — such a tool keeps the
 * selection when opened, so reaching for a feature's tool from its selection
 * toolbar doesn't throw the selection away.
 */
function ownsSelection(tool: Tool, selection: Selection | null): boolean {
  switch (selection?.type) {
    case 'feature':
    case 'vertex':
      return isDrawTool(tool);

    case 'objects':
      return tool === 'objects';

    case 'tracking':
      return tool === 'tracking';

    case 'route-point':
    case 'route-leg':
      return tool === 'route-planner';

    default:
      return false;
  }
}

/**
 * The tool a conversion to drawing was reached for from — its toolbar acted on
 * features that are a drawing now, so it goes with them. A search result is
 * converted from its selection toolbar, which belongs to no tool.
 */
const convertSourceTools: Record<
  ReturnType<typeof convertToDrawing>['payload']['type'],
  Tool | undefined
> = {
  objects: 'objects',
  'objects-geometry': 'objects',
  'planned-route': 'route-planner',
  changesets: 'changesets',
  'search-result': undefined,
  geojson: undefined,
};

export const mainReducer = createReducer(mainInitialState, (builder) => {
  builder
    .addCase(openTool, (state, action) => {
      if (window.fmEmbedded) {
        return;
      }

      const tool = action.payload;

      if (!isMapClickTool(tool)) {
        if (!state.panelTools.includes(tool)) {
          state.panelTools.push(tool);
        }

        return;
      }

      // Drawing is a mode of the map features' toolbar, which comes up with it.
      if (isDrawTool(tool) && !state.panelTools.includes('import-file')) {
        state.panelTools.push('import-file');
      }

      // Taking the map-click slot from another tool leaves the selected feature
      // behind; reopening the one the feature belongs to keeps it, so reaching
      // for a feature's tool from its selection toolbar doesn't throw the
      // selection away. Opening a toolbar-only tool says nothing about the
      // selection at all.
      if (tool !== state.mapTool && !ownsSelection(tool, state.selection)) {
        state.selection = null;
      }

      state.mapTool = tool;
    })
    .addCase(closeTool, (state, action) => {
      const tool = action.payload;

      // Its toolbar holds the draw modes, so they go with it.
      if (
        state.mapTool === tool ||
        (tool === 'import-file' && isDrawTool(state.mapTool))
      ) {
        state.mapTool = null;
      }

      if (state.panelTools.includes(tool)) {
        state.panelTools = state.panelTools.filter((t) => t !== tool);
      }
    })
    .addCase(clearMapFeatures, (state) => {
      state.selection = null;
    })
    // An element belongs to the changeset inspected; another one has others.
    .addCase(changesetsInspect, (state) => {
      if (state.selection?.type === 'changeset-element') {
        state.selection = null;
      }
    })
    // Back from a history's changeset to its element, selected and detailed.
    .addCase(changesetsReturnToHistory, (state, { payload }) => {
      state.selection = payload.selection;

      state.detailsShown = true;

      state.detailsOnRequest = false;
    })
    .addCase(setActiveModal, (state, action) => {
      state.activeModal = action.payload;
    })
    .addCase(documentShow, (state, action) => {
      state.activeModal = action.payload
        ? { type: 'document', key: action.payload }
        : null;
    })
    .addCase(setErrorTicketId, (state, action) => {
      state.errorTicketId = action.payload;
    })
    .addCase(setEmbedFeatures, (state, action) => {
      state.embedFeatures = action.payload;
    })
    // Only a result selects: clearing one leaves the selection to whoever
    // cleared it, so a `search` selection can't outlive the result behind it.
    .addCase(searchSelectResult, (state, action) => {
      const { payload } = action;

      if (payload && (payload.select ?? true)) {
        const same =
          state.selection?.type === 'search' &&
          featureIdsEqual(state.selection.id, payload.result.id);

        state.selection = {
          type: 'search',
          id: payload.result.id,
        };

        // Re-picking the selected feature opens its details, never closes them,
        // and keeps its toggle.
        const shows = payload.details ?? true;

        state.detailsOnRequest = (same && state.detailsOnRequest) || !shows;

        state.detailsShown = (same && state.detailsShown) || shows;
      }
    })
    // The other shown results stay, but nothing is acted upon until one of them
    // is picked again — which one would be a guess.
    .addCase(searchUnselectResult, (state, action) => {
      if (
        state.selection?.type === 'search' &&
        featureIdsEqual(state.selection.id, action.payload)
      ) {
        state.selection = null;
      }
    })
    .addCase(selectFeature, (state, action) => {
      if (
        window.fmEmbedded &&
        action.payload &&
        action.payload.type !== 'tracking'
      ) {
        return;
      }

      state.selection = action.payload;

      // Re-selecting the selected feature is how its details come back after ×.
      state.detailsShown = true;

      state.detailsOnRequest = false;
    })
    .addCase(selectionRenumbered, (state, action) => {
      state.selection = action.payload;
    })
    // The inner polygon becomes its parent's hole and leaves the list.
    .addCase(dataViewerMakeHole, (state, { payload: { index, parent } }) => {
      const { selection } = state;

      if (selection?.type !== 'feature' && selection?.type !== 'vertex') {
        return;
      }

      const shifted = (id: number) => (id > index ? id - 1 : id);

      state.selection =
        selection.id === index
          ? { type: 'feature', id: shifted(parent) }
          : { ...selection, id: shifted(selection.id) };
    })
    // The ring becomes a polygon of its own, right after its parent.
    .addCase(dataViewerDetachHole, (state, { payload: { index, part } }) => {
      const { selection } = state;

      if (selection?.type !== 'feature' && selection?.type !== 'vertex') {
        return;
      }

      if (selection.id > index) {
        state.selection = { ...selection, id: selection.id + 1 };
      } else if (selection.id === index && selection.part !== undefined) {
        state.selection =
          selection.part === part
            ? { type: 'feature', id: index + 1 }
            : selection.part > part
              ? { ...selection, part: selection.part - 1 }
              : selection;
      }
    })
    .addCase(setDetailsShown, (state, action) => {
      state.detailsShown = action.payload;
    })
    .addCase(convertToDrawing, (state, action) => {
      const { payload } = action;

      // Converting one feature leaves the tool it came from with the rest of
      // them; only converting everything it holds takes the tool with it.
      if ('id' in payload && payload.id !== undefined) {
        return;
      }

      const source = convertSourceTools[payload.type];

      if (state.mapTool === source) {
        state.mapTool = null;
      }

      if (source && state.panelTools.includes(source)) {
        state.panelTools = state.panelTools.filter((t) => t !== source);
      }
    })
    // An object that is no longer among the found ones can't be the selected
    // feature: nothing would render its toolbar, so the selection would sit
    // there with no way to reach it and no way to let it go. Reached by the
    // filter being cleared and by the objects being handed to another feature;
    // panning keeps the selected one (`keepSelected`).
    .addCase(objectsSetResult, (state, action) => {
      const { selection } = state;

      if (
        selection?.type === 'objects' &&
        !action.payload.some((object) =>
          featureIdsEqual(object.id, selection.id),
        )
      ) {
        state.selection = null;
      }
    })
    .addCase(trackingActions.setTrackedDevices, (state, action) => {
      const { selection } = state;

      if (
        selection?.type === 'tracking' &&
        !action.payload.some((device) => device.token === selection.id)
      ) {
        state.selection = null;
      }
    })
    .addCase(trackingActions.delete, (state, { payload }) => {
      clearTrackingSelection(state, payload.token);
    })
    .addCase(trackingActions.deleteTrackedDevice, (state, { payload }) => {
      clearTrackingSelection(state, payload);
    })
    .addCase(hideInfoBar, (state, action) => {
      state.hiddenInfoBars[action.payload.key] = action.payload.ts;
    })
    .addCase(infoBarShown, (state, action) => {
      state.shownInfoBars[action.payload.key] = action.payload.ts;
    })
    .addCase(routePlannerAddPoint, (state, action) => {
      return {
        ...state,
        selection: { type: 'route-point', id: action.payload.position + 1 },
      };
    })
    // The route's own toolbar deletes it directly, so what it deletes must not
    // stay selected. Only a route selection goes: `convertToDrawing` deletes the
    // route too, after selecting the drawing line it turned into.
    .addCase(routePlannerDelete, (state) => {
      if (
        state.selection?.type === 'route-point' ||
        state.selection?.type === 'route-leg'
      ) {
        state.selection = null;
      }
    })
    .addCase(routePlannerSetPoint, (state, action) => {
      return action.payload.preventSelect
        ? state
        : {
            ...state,
            selection: { type: 'route-point', id: action.payload.position },
          };
    })
    // Loading renumbers the features and deleting takes them away, so a
    // selection that survived either would name another feature or none. What
    // a fresh load selects instead is `dataViewerSetTrackDataProcessor`.
    .addMatcher(
      isAnyOf(
        dataViewerSetData,
        dataViewerEditFeatures,
        dataViewerDelete,
        dataViewerDeleteFeature,
      ),
      (state) => {
        if (
          state.selection?.type === 'feature' ||
          state.selection?.type === 'vertex'
        ) {
          state.selection = null;
        }
      },
    )
    .addMatcher(authWithPopupOAuth.match, (state, action) => {
      // A modal that started the authorization for its own follow-up stays
      // open, showing it as its busy state (`OAUTH_PROGRESS_ID`).
      if (
        !action.payload.successAction ||
        state.activeModal?.type === 'login'
      ) {
        state.activeModal = null;
      }
    })
    .addMatcher(
      isAnyOf(authWithFacebook, authWithGoogle, authWithGarmin, authWithApple),
      (state) => {
        state.activeModal = null;
      },
    );
});
